import type { Policies } from "../../../api/adminPolicies";
import { MIN_BY_KEY } from "./policySections";

/** The policies as typed in the form, one string per field. */
export type PolicyValues = Record<keyof Policies, string>;

/** The range the server accepts for a field, when it refused the value typed. */
export interface AllowedRange {
  min: string;
  max: string;
}

const DEFAULT_MIN = 1;
const WHOLE_NUMBER = /^\d+$/;
const RANGE_VIOLATION = /^([a-zA-Z]+)\.range-(-?\d+)-(-?\d+)$/;

export function toValues(policies: Policies): PolicyValues {
  return Object.fromEntries(Object.entries(policies).map(([k, v]) => [k, String(v)])) as PolicyValues;
}

export function toPolicies(values: PolicyValues): Policies {
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [k, Number(v)])) as unknown as Policies;
}

/** Keeps only the digits of what was typed. */
export function digitsOnly(text: string): string {
  return text.replace(/[^\d]/g, "");
}

/** The fields whose value differs from the saved one. */
export function changedKeys(values: PolicyValues, saved: Policies): (keyof Policies)[] {
  return (Object.keys(saved) as (keyof Policies)[]).filter((k) => values[k] !== String(saved[k]));
}

/** The message of every field that isn't a whole number at or above its minimum. */
export function invalidFields(values: PolicyValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    if (v.trim() === "" || !WHOLE_NUMBER.test(v.trim())) out[k] = "Enter a whole number.";
    else if (Number(v) < (MIN_BY_KEY[k] ?? DEFAULT_MIN)) out[k] = "It must be greater than zero.";
  }
  return out;
}

/** The allowed range per field from the server's `<field>.range-<min>-<max>` violation codes. */
export function rangesFromViolations(codes: string[]): Record<string, AllowedRange> {
  const ranges: Record<string, AllowedRange> = {};
  for (const code of codes) {
    const match = RANGE_VIOLATION.exec(code);
    const [, field, min, max] = match ?? [];
    if (field && min && max) ranges[field] = { min, max };
  }
  return ranges;
}
